const WINDOW_HALF = 0.16;
const CROSS_SECONDS = 1.6;
// A full cycle is one there-and-back oscillation of the ball. We only need
// roughly 1.5 oscillations worth of valid in/out data, so require 2 valid
// cycles but shorten the oscillation so total time stays modest.
const REQUIRED_VALID_CYCLES = 2;
// Absolute floor guard so pure silence/DC is never treated as signal even
// when gain is very high.
const ABS_SIGNAL_FLOOR = 0.02;
// In-window energy must beat the measured out-of-window (quiet) energy by
// this ratio to count as "making the sound". Using a RELATIVE measure means
// an unusually high gain (which lifts every frame) still calibrates fine, as
// long as the voice frames are clearly louder than the quiet frames.
const SIGNAL_OVER_QUIET = 1.8;
export class AcSustainPanel extends HTMLElement {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "clusterId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "hum"
        });
        Object.defineProperty(this, "raf", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "running", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "startTs", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "ballX", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0.5
        });
        Object.defineProperty(this, "inWindow", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "holding", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "cycles", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "lastPhase", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "inWindowEnergySum", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "inWindowTotalFrames", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "outWindowEnergySum", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "outWindowTotalFrames", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "validCycles", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
    }
    connectedCallback() {
        this.clusterId = this.getAttribute("cluster") ?? "hum";
        this.style.position = "fixed";
        this.style.inset = "0";
        this.style.zIndex = "1100";
        this.style.pointerEvents = "auto";
        this.render();
        this.start();
    }
    disconnectedCallback() {
        this.stopHold();
        this.stop();
    }
    update(_snap) {
        const energy = _snap.features?.energy ?? 0;
        if (this.inWindow) {
            this.inWindowTotalFrames += 1;
            this.inWindowEnergySum += energy;
        }
        else {
            this.outWindowTotalFrames += 1;
            this.outWindowEnergySum += energy;
        }
        this.updateReadout();
    }
    start() {
        this.running = true;
        this.startTs =
            typeof performance !== "undefined" ? performance.now() : Date.now();
        const loop = () => {
            if (!this.running)
                return;
            this.tick();
            this.raf = requestAnimationFrame(loop);
        };
        this.raf = requestAnimationFrame(loop);
    }
    stop() {
        this.running = false;
        cancelAnimationFrame(this.raf);
        this.raf = 0;
    }
    startHold() {
        if (this.holding)
            return;
        this.holding = true;
        this.dispatchEvent(new CustomEvent("sustain-hold-start", {
            detail: { clusterId: this.clusterId },
            bubbles: true,
        }));
    }
    stopHold() {
        if (!this.holding)
            return;
        this.holding = false;
        this.dispatchEvent(new CustomEvent("sustain-hold-end", {
            detail: { clusterId: this.clusterId },
            bubbles: true,
        }));
    }
    tick() {
        const now = typeof performance !== "undefined" ? performance.now() : Date.now();
        const t = (now - this.startTs) / 1000;
        const period = CROSS_SECONDS * 2;
        const phase = (t % period) / period;
        this.ballX = phase < 0.5 ? phase * 2 : 2 - phase * 2;
        const nowIn = Math.abs(this.ballX - 0.5) <= WINDOW_HALF;
        if (nowIn && !this.inWindow)
            this.startHold();
        else if (!nowIn && this.inWindow)
            this.stopHold();
        this.inWindow = nowIn;
        if (phase < this.lastPhase)
            this.onCycleComplete();
        this.lastPhase = phase;
        this.draw(now);
    }
    onCycleComplete() {
        this.cycles += 1;
        const inMean = this.inWindowTotalFrames > 0
            ? this.inWindowEnergySum / this.inWindowTotalFrames
            : 0;
        const outMean = this.outWindowTotalFrames > 0
            ? this.outWindowEnergySum / this.outWindowTotalFrames
            : 0;
        // Valid when the in-window (voice) energy is clearly above the
        // absolute floor AND meaningfully louder than the out-of-window
        // (quiet) energy. This is robust to unusually high gain, which lifts
        // every frame's energy but preserves the voice↔quiet contrast.
        const valid = inMean >= ABS_SIGNAL_FLOOR && inMean >= outMean * SIGNAL_OVER_QUIET;
        if (valid) {
            this.validCycles += 1;
            console.info(`[ac-sustain-panel] valid cycle ${this.validCycles}/` +
                `${REQUIRED_VALID_CYCLES} (inMean=${inMean.toFixed(3)} ` +
                `outMean=${outMean.toFixed(3)})`);
        }
        else {
            console.info(`[ac-sustain-panel] INVALID cycle rejected ` +
                `(inMean=${inMean.toFixed(3)} outMean=${outMean.toFixed(3)})`);
        }
        this.inWindowEnergySum = 0;
        this.inWindowTotalFrames = 0;
        this.outWindowEnergySum = 0;
        this.outWindowTotalFrames = 0;
        this.updateReadout();
        if (this.validCycles >= REQUIRED_VALID_CYCLES) {
            this.autoAdvance();
        }
    }
    autoAdvance() {
        console.info("[ac-sustain-panel] enough valid data collected — auto-advancing");
        this.stopHold();
        this.dispatchEvent(new CustomEvent("sustain-done", {
            detail: { clusterId: this.clusterId },
            bubbles: true,
        }));
    }
    draw(now) {
        void now;
        const canvas = this.querySelector("[data-sustain-canvas]");
        if (!canvas)
            return;
        const ctx = canvas.getContext("2d");
        if (!ctx)
            return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const cw = canvas.clientWidth || 520;
        const ch = canvas.clientHeight || 120;
        if (canvas.width !== Math.floor(cw * dpr) ||
            canvas.height !== Math.floor(ch * dpr)) {
            canvas.width = Math.floor(cw * dpr);
            canvas.height = Math.floor(ch * dpr);
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cw, ch);
        const pad = 24;
        const trackY = ch / 2;
        const trackW = cw - pad * 2;
        const x0 = pad;
        ctx.strokeStyle = "rgba(255,255,255,0.15)";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(x0, trackY);
        ctx.lineTo(x0 + trackW, trackY);
        ctx.stroke();
        const winCx = x0 + trackW * 0.5;
        const winW = trackW * (WINDOW_HALF * 2);
        ctx.fillStyle = this.inWindow
            ? "rgba(74,222,128,0.18)"
            : "rgba(34,211,238,0.14)";
        ctx.fillRect(winCx - winW / 2, pad, winW, ch - pad * 2);
        ctx.strokeStyle = this.inWindow
            ? "rgba(74,222,128,0.7)"
            : "rgba(34,211,238,0.5)";
        ctx.lineWidth = 2;
        ctx.strokeRect(winCx - winW / 2, pad, winW, ch - pad * 2);
        ctx.fillStyle = "rgba(255,255,255,0.55)";
        ctx.font = "600 12px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(this.inWindow ? "MAKE THE SOUND" : "STAY QUIET", winCx, pad - 8);
        const bx = x0 + trackW * this.ballX;
        const color = this.inWindow ? "#4ade80" : "#22d3ee";
        ctx.shadowColor = color;
        ctx.shadowBlur = this.inWindow ? 22 : 10;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(bx, trackY, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
    }
    updateReadout() {
        const el = this.querySelector("[data-sustain-readout]");
        if (el) {
            el.innerHTML =
                `<span style="opacity:.8">Valid: ` +
                    `<b>${this.validCycles}</b>/${REQUIRED_VALID_CYCLES}` +
                    `</span> &nbsp;·&nbsp; ` +
                    `<span style="opacity:.6">passes ${this.cycles}</span>`;
        }
        const done = this.querySelector("[data-sustain-done]");
        if (done) {
            done.disabled = this.validCycles < 1;
            done.style.opacity = this.validCycles < 1 ? ".5" : "1";
        }
    }
    render() {
        const glyph = this.getAttribute("glyph") ?? "🎵";
        const label = this.getAttribute("label") ?? this.clusterId;
        this.innerHTML = `
                  <div style="position:absolute;inset:0;background:rgba(0,0,0,.6);
                      display:flex;align-items:center;justify-content:center">
                    <div style="background:#0d1024;border:1px solid #2a2f4a;border-radius:16px;
                        width:min(92vw,560px);color:#fff;padding:20px 22px">
                      <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px">
                        <span style="font-size:20px">${glyph}</span>
                        <span style="flex:1;font-size:18px;font-weight:800">Train: ${label}</span>
                        <button data-sustain-cancel
                            style="pointer-events:auto;cursor:pointer;padding:6px 12px;
                            border-radius:8px;border:1px solid #f87171;background:#1a1f3a;
                            color:#fff;font-size:13px;font-weight:600">Cancel</button>
                      </div>
                      <p style="opacity:.8;font-size:13px;margin:0 0 14px;line-height:1.5">
                        Hold the sound steadily while the ball is inside the
                        highlighted window, and <b>stay quiet</b> while it is outside.
                        Quiet gaps are captured as silence training.
                      </p>
                      <canvas data-sustain-canvas
                          style="display:block;width:100%;height:120px;border-radius:12px;
                          background:radial-gradient(circle at 50% 50%,rgba(34,211,238,.08),rgba(10,12,24,.9))"></canvas>
                      <div data-sustain-readout style="text-align:center;margin-top:12px;
                          font-size:13px;font-weight:600">
                        <span style="opacity:.8">Cycles: <b>0</b></span>
                      </div>
                      <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:16px">
                       <button data-sustain-skip
                           style="pointer-events:auto;cursor:pointer;padding:8px 16px;
                           border-radius:10px;border:1px solid #fbbf24;background:#1a1f3a;
                           color:#fff;font-size:14px;font-weight:700;margin-right:auto">
                           Skip this sound</button>
                        <button data-sustain-done disabled
                            style="pointer-events:auto;cursor:pointer;padding:8px 16px;
                            border-radius:10px;border:1px solid #4ade80;background:#1a1f3a;
                            color:#fff;font-size:14px;font-weight:700;opacity:.5">
                            Next</button>
                      </div>
                    </div>
                  </div>`;
        this.querySelector("[data-sustain-cancel]")?.addEventListener("click", () => {
            this.stopHold();
            this.dispatchEvent(new CustomEvent("sustain-cancel", {
                detail: { clusterId: this.clusterId },
                bubbles: true,
            }));
        });
        this.querySelector("[data-sustain-done]")?.addEventListener("click", () => {
            this.stopHold();
            this.dispatchEvent(new CustomEvent("sustain-done", {
                detail: { clusterId: this.clusterId },
                bubbles: true,
            }));
        });
        this.querySelector("[data-sustain-skip]")?.addEventListener("click", () => {
            this.stopHold();
            console.info(`[ac-sustain-panel] user skipped calibrating ` + `'${this.clusterId}'`);
            this.dispatchEvent(new CustomEvent("sustain-skip", {
                detail: { clusterId: this.clusterId },
                bubbles: true,
            }));
        });
    }
}
customElements.define("ac-sustain-panel", AcSustainPanel);
